import {useCallback} from 'react';
import styled from 'styled-components';

import Avatar from '../../Avatar';
import {
  fetchOutlookCompanyAutocomplete,
  OutlookCompanyJson,
} from '../../requests';
import {usePodRequest} from '../../usePodRequest';
import EntityContainer from './EntityContainer';
import EntityLink from './EntityLink';
import LinkSelector from './LinkSelector';
import {CompanyLink, MessageAttachment} from './structs';

interface Props {
  agencyId: number;
  companyLinks: CompanyLink[];
  setCompanyLinks: (companyLinks: CompanyLink[]) => void;
  attachments: MessageAttachment[];
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CompanyLinks = ({
  agencyId,
  companyLinks,
  setCompanyLinks,
  attachments,
}: Props) => {
  const {request} = usePodRequest();

  const linkedCompanyIds = new Set(companyLinks.map((link) => link.company.id));

  const searchCompanies = useCallback(
    async (query: string) => {
      const {companies} = await fetchOutlookCompanyAutocomplete(
        request,
        agencyId,
        {query}
      );
      return companies;
    },
    [agencyId, request]
  );

  const handleSelect = (company: OutlookCompanyJson) => {
    if (linkedCompanyIds.has(company.id)) {
      return;
    }
    setCompanyLinks([
      ...companyLinks,
      {company, pinned: false, attachmentIds: []},
    ]);
  };

  const handleTogglePinned = (companyId: number) => {
    setCompanyLinks(
      companyLinks.map((link) =>
        link.company.id === companyId ? {...link, pinned: !link.pinned} : link
      )
    );
  };

  const handleRemove = (companyId: number) => {
    setCompanyLinks(
      companyLinks.filter((link) => link.company.id !== companyId)
    );
  };

  const handleAttachmentsChanged = (
    companyId: number,
    attachmentIds: string[]
  ) => {
    setCompanyLinks(
      companyLinks.map((link) =>
        link.company.id === companyId ? {...link, attachmentIds} : link
      )
    );
  };

  return (
    <Container>
      <LinkSelector
        title="Companies"
        placeholder="Add Companies..."
        searchOptions={searchCompanies}
        getOptionKey={(company) => company.id}
        renderOption={(company) => company.name ?? ''}
        onSelect={handleSelect}
        excludeKeys={linkedCompanyIds}
      />
      {companyLinks.length > 0 && (
        <EntityContainer>
          {companyLinks.map((link) => (
            <EntityLink
              avatar={
                <Avatar
                  name={link.company.name ?? ''}
                  imageUrl={link.company.logo_thumb_url}
                  size="sm"
                />
              }
              key={link.company.id}
              name={link.company.name ?? ''}
              attachmentIds={link.attachmentIds}
              allAttachments={attachments}
              showPinned
              pinned={link.pinned}
              attachmentType="Attachment"
              togglePinned={() => handleTogglePinned(link.company.id)}
              remove={() => handleRemove(link.company.id)}
              onAttachmentsChanged={(attachmentIds: string[]) =>
                handleAttachmentsChanged(link.company.id, attachmentIds)
              }
            />
          ))}
        </EntityContainer>
      )}
    </Container>
  );
};

export default CompanyLinks;
